// src/views/parent/PAContact.jsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useParentService } from "../../services/parentService";
import { useParentDrawer } from "../../context/ParentDrawerContext";

// ─── TOAST ──────────────────────────────────────────────────────────────
function Toast({ message, onClose }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="toast-container">
      <div className="toast-content" style={{ background: "var(--rd)" }}>
        <div className="toast-icon">
          <i className="ti ti-x" />
        </div>
        <div className="toast-text">
          <div className="toast-message">{message}</div>
        </div>
        <button className="toast-close" onClick={onClose}>
          <i className="ti ti-x" />
        </button>
      </div>
    </div>
  );
}

// ─── CARTE DE CONTACT ─────────────────────────────────────────────────
function ContactCard({ icon, iconColor, title, subtitle, value, actionLabel, onAction }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className="contact-card"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="card-header">
        <div className="card-icon" style={{ background: `${iconColor}15` }}>
          <i className={`ti ${icon}`} style={{ color: iconColor, fontSize: 20 }} />
        </div>
        <div>
          <h3>{title}</h3>
          <p>{subtitle}</p>
        </div>
      </div>

      <button className="card-value" onClick={onAction}>
        <i className={`ti ${icon}`} style={{ color: iconColor, fontSize: 18 }} />
        <span>{value}</span>
        <i className="ti ti-arrow-right" />
      </button>

      <button className="card-action" style={{ background: iconColor }} onClick={onAction}>
        <i className={`ti ${icon}`} />
        <span>{actionLabel}</span>
      </button>
    </div>
  );
}

// ─── CARTE RÉSEAUX SOCIAUX ────────────────────────────────────────────
function SocialCard({ onFacebook, onWhatsApp, onWebsite, etablissement }) {
  const [hovered, setHovered] = useState(false);

  const socialButtons = [
    { icon: "ti ti-brand-facebook", color: "#1877F2", label: "Facebook", onClick: onFacebook },
    { icon: "ti ti-brand-whatsapp", color: "#25D366", label: "WhatsApp", onClick: onWhatsApp },
    { icon: "ti ti-world", color: "#64748b", label: "Site web", onClick: onWebsite },
  ];

  return (
    <div
      className="social-card"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="card-header">
        <div className="card-icon" style={{ background: "var(--pr-bg)" }}>
          <i className="ti ti-share" style={{ color: "var(--pr)", fontSize: 20 }} />
        </div>
        <div>
          <h3>Nous suivre</h3>
          <p>Restez connecté avec {etablissement?.nom || "nous"} sur les réseaux sociaux</p>
        </div>
      </div>

      <div className="social-buttons">
        {socialButtons.map((btn, idx) => (
          <button
            key={idx}
            className="social-btn"
            style={{ background: `${btn.color}10` }}
            onClick={btn.onClick}
          >
            <i className={btn.icon} style={{ color: btn.color }} />
            <span style={{ color: btn.color }}>{btn.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── HEADER ──────────────────────────────────────────────────────────────
function ContactHeader({ etablissement, loading, slug }) {
  const navigate = useNavigate();
  const { openDrawer } = useParentDrawer();
  const nomEcole = etablissement?.nom || "Suivi Scolaire";

  return (
    <div className="contact-header">
      {/* Bouton Menu (Drawer) */}
      <button className="menu-btn" onClick={openDrawer}>
        <i className="ti ti-menu-2" />
      </button>

      <div className="header-content">
        <div className="icon-wrapper">
          <i className="ti ti-phone" />
        </div>
        <h1>Nous contacter</h1>
        <p>{loading ? "Chargement..." : `${nomEcole} - Une question ? Notre équipe est là pour vous répondre`}</p>
      </div>
    </div>
  );
}

// ─── PAGE PRINCIPALE ──────────────────────────────────────────────────
export default function PAContact({ slug }) {
  const navigate = useNavigate();
  const { getEtablissement } = useParentService();
  const [errorMessage, setErrorMessage] = useState(null);
  const [etablissement, setEtablissement] = useState(null);
  const [loading, setLoading] = useState(true);

  // ─── CHARGEMENT DES INFOS ──────────────────────────────────────────
  useEffect(() => {
    const fetchEtablissement = async () => {
      if (!slug) return;

      try {
        const data = await getEtablissement(slug);
        setEtablissement(data);
      } catch (err) {
        // Utiliser des valeurs par défaut en cas d'erreur
        setEtablissement({
          nom: "Suivi Scolaire",
          telephone1: "60 01 30 10",
          email: "centrekamalpha@gmail.com",
          site_web: "https://www.suivapp.com",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchEtablissement();
  }, [slug]);

  const handleLogout = () => {
    localStorage.removeItem("authTokens");
    window.location.href = "/login";
  };

  const showError = (message) => {
    setErrorMessage(message);
  };

  // Fonctions d'appel avec les données dynamiques
  const makePhoneCall = () => {
    const phone = etablissement?.telephone1 || etablissement?.telephone || "22660013010";
    const cleanPhone = phone.replace(/\s/g, '');
    window.location.href = `tel:+${cleanPhone}`;
  };

  const sendEmail = () => {
    const email = etablissement?.email || "centrekamalpha@gmail.com";
    window.location.href = `mailto:${email}?subject=&body=`;
  };

  const openFacebook = () => {
    const url = etablissement?.facebook || "https://www.facebook.com";
    window.open(url, "_blank");
  };

  const openWhatsApp = () => {
    const phone = etablissement?.telephone1 || etablissement?.telephone || "22660013010";
    const cleanPhone = phone.replace(/\s/g, '');
    window.open(`https://wa.me/${cleanPhone}`, "_blank");
  };

  const openWebsite = () => {
    const url = etablissement?.site_web || "https://www.suivapp.com";
    window.open(url, "_blank");
  };

  if (loading) {
    return (
      <div className="loader-container">
        <div className="loader-spinner"></div>
      </div>
    );
  }

  const telephone = etablissement?.telephone1 || etablissement?.telephone || "60 01 30 10";
  const telephoneFull = etablissement?.telephone1 || etablissement?.telephone
    ? `+226 ${telephone}`
    : "+226 60 01 30 10";
  const email = etablissement?.email || "centrekamalpha@gmail.com";
  const siteWeb = etablissement?.site_web || "https://www.suivapp.com";

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Inter:wght@400;500;600;700&display=swap');

        *, *::before, *::after { box-sizing: border-box; }

        .contact-page {
          min-height: 100vh;
          background: var(--s2);
        }

        .contact-header {
          height: 240px;
          flex-shrink: 0;
          background: linear-gradient(135deg, var(--pr-dk), var(--pr));
          border-radius: 0 0 40px 40px;
          box-shadow: 0 4px 15px rgba(0,0,0,0.15);
          position: relative;
          overflow: hidden;
        }

        .contact-header::after {
          content: "";
          position: absolute;
          left: 20%;
          right: 20%;
          bottom: 0;
          height: 3px;
          background: linear-gradient(90deg, transparent, var(--or), transparent);
        }

        .contact-header .menu-btn {
          position: absolute;
          top: 44px;
          left: 16px;
          background: rgba(255,255,255,0.2);
          border: none;
          border-radius: 12px;
          padding: 8px 10px;
          cursor: pointer;
          color: #fff;
          font-size: 20px;
          width: 42px;
          height: 42px;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10;
          transition: background 0.15s;
        }

        .contact-header .menu-btn:hover {
          background: rgba(255,255,255,0.3);
        }

        .contact-header .header-content {
          position: absolute;
          bottom: 24px;
          left: 0;
          right: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          padding: 0 20px;
        }

        .contact-header .header-content .icon-wrapper {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          background: rgba(255,255,255,0.2);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .contact-header .header-content .icon-wrapper i {
          font-size: 28px;
          color: #fff;
        }

        .contact-header .header-content h1 {
          font-family: 'Poppins', sans-serif;
          color: #fff;
          font-size: 24px;
          font-weight: 700;
          margin: 0;
        }

        .contact-header .header-content p {
          font-family: 'Inter', sans-serif;
          color: rgba(255,255,255,0.8);
          font-size: 13px;
          margin: 0;
        }

        .contact-content {
          padding: 16px;
        }

        .contact-card {
          background: #fff;
          border-radius: 20px;
          padding: 18px;
          box-shadow: 0 4px 10px rgba(0,0,0,0.06);
          transition: all 0.22s ease;
          margin-bottom: 14px;
          animation: fadeUp 0.4s ease both;
        }

        .contact-card:hover {
          transform: translateY(-2px);
        }

        .contact-card .card-header {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 14px;
        }

        .contact-card .card-header .card-icon {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .contact-card .card-header h3 {
          font-family: 'Poppins', sans-serif;
          font-size: 16px;
          font-weight: 700;
          color: var(--t);
          margin: 0 0 2px;
        }

        .contact-card .card-header p {
          font-family: 'Inter', sans-serif;
          font-size: 12px;
          color: var(--m);
          margin: 0;
        }

        .contact-card .card-value {
          width: 100%;
          padding: 12px 14px;
          background: var(--s2);
          border: 1px solid var(--b);
          border-radius: 10px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 12px;
          transition: all 0.2s;
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          font-weight: 500;
          color: var(--t);
        }

        .contact-card .card-value:hover {
          background: var(--b);
          border-color: var(--pr);
        }

        .contact-card .card-value span {
          flex: 1;
          text-align: left;
        }

        .contact-card .card-value i:last-child {
          font-size: 14px;
          color: var(--m);
        }

        .contact-card .card-action {
          width: 100%;
          padding: 10px;
          border: none;
          border-radius: 30px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: all 0.2s;
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          font-weight: 600;
          color: #fff;
        }

        .contact-card .card-action:hover {
          opacity: 0.9;
          transform: translateY(-1px);
        }

        .social-card {
          background: #fff;
          border-radius: 20px;
          padding: 18px;
          box-shadow: 0 4px 10px rgba(0,0,0,0.06);
          transition: all 0.22s ease;
          animation: fadeUp 0.4s ease both;
          animation-delay: 0.1s;
        }

        .social-card:hover {
          transform: translateY(-2px);
        }

        .social-card .card-header {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 14px;
        }

        .social-card .card-header .card-icon {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .social-card .card-header h3 {
          font-family: 'Poppins', sans-serif;
          font-size: 16px;
          font-weight: 700;
          color: var(--t);
          margin: 0 0 2px;
        }

        .social-card .card-header p {
          font-family: 'Inter', sans-serif;
          font-size: 12px;
          color: var(--m);
          margin: 0;
        }

        .social-card .social-buttons {
          display: flex;
          gap: 10px;
        }

        .social-card .social-buttons .social-btn {
          flex: 1;
          padding: 10px 8px;
          border: none;
          border-radius: 10px;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          transition: all 0.2s;
          font-family: 'Inter', sans-serif;
          font-size: 11px;
          font-weight: 500;
        }

        .social-card .social-buttons .social-btn:hover {
          transform: translateY(-2px);
        }

        .social-card .social-buttons .social-btn i {
          font-size: 20px;
        }

        .toast-container {
          position: fixed;
          top: 80px;
          left: 16px;
          right: 16px;
          z-index: 2000;
          animation: slideDown 0.3s ease-out;
          max-width: 430px;
          margin: 0 auto;
        }

        .toast-content {
          padding: 14px 18px;
          border-radius: 16px;
          box-shadow: 0 8px 20px rgba(0,0,0,0.15);
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .toast-icon {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: rgba(255,255,255,0.2);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .toast-icon i {
          color: #fff;
          font-size: 18px;
        }

        .toast-text {
          flex: 1;
        }

        .toast-message {
          font-family: 'Inter', sans-serif;
          color: #fff;
          font-weight: 700;
          font-size: 14px;
        }

        .toast-close {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: rgba(255,255,255,0.2);
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .toast-close i {
          color: rgba(255,255,255,0.8);
          font-size: 16px;
        }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @media (max-width: 480px) {
          .contact-header {
            height: 200px;
          }
          .contact-header .header-content h1 {
            font-size: 20px;
          }
          .contact-content {
            padding: 12px;
          }
          .social-card .social-buttons {
            flex-direction: column;
          }
        }
      `}</style>

      <div className="contact-page">
        <ContactHeader
          etablissement={etablissement}
          loading={loading}
          slug={slug}
        />

        {errorMessage && (
          <Toast message={errorMessage} onClose={() => setErrorMessage(null)} />
        )}

        <div className="contact-content">
          {/* Carte Téléphone */}
          <div style={{ animationDelay: "0s" }}>
            <ContactCard
              icon="ti-phone"
              iconColor="#3b6d11"
              title="Assistance téléphonique"
              subtitle="Appelez-nous pour une assistance immédiate"
              value={telephoneFull}
              actionLabel="Appeler"
              onAction={makePhoneCall}
            />
          </div>

          {/* Carte Email */}
          <div style={{ animationDelay: "0.05s" }}>
            <ContactCard
              icon="ti-mail"
              iconColor="#dc3545"
              title="Support par email"
              subtitle="Envoyez-nous vos questions"
              value={email}
              actionLabel="Envoyer un email"
              onAction={sendEmail}
            />
          </div>

          {/* Carte Réseaux sociaux */}
          <div style={{ animationDelay: "0.1s" }}>
            <SocialCard
              onFacebook={openFacebook}
              onWhatsApp={openWhatsApp}
              onWebsite={openWebsite}
              etablissement={etablissement}
            />
          </div>
        </div>
      </div>
    </>
  );
}